import {CommonModule, CurrencyPipe, DecimalPipe} from '@angular/common';
import {AfterViewInit, Component, Input, OnChanges, SimpleChanges, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {GoogleMap, MapInfoWindow, MapMarker} from '@angular/google-maps';
import {RealEstate} from '../../interfaces/real-estate';

interface NearbyPlace {
  name: string;
  distanceKm: number;
  neighborhood?: string;
  coordinates: {
    lat: number;
    lng: number;
  };
}

interface NearbyPlaceCategory {
  id: string;
  title: string;
  markerLabel: string;
  googleTypes: string[];
  googlePrimaryTypes: string[];
}

@Component({
  selector: 'app-real-estate-nearby-places',
  imports: [CommonModule, CurrencyPipe, DecimalPipe, FormsModule, GoogleMap, MapInfoWindow, MapMarker],
  templateUrl: './real-estate-nearby-places.component.html',
  styleUrl: './real-estate-nearby-places.component.css'
})
export class RealEstateNearbyPlacesComponent implements AfterViewInit, OnChanges {
  @Input({ required: true }) realEstate!: RealEstate;
  @ViewChild('nearbyPlacesMap') nearbyPlacesMap?: GoogleMap;
  @ViewChild('propertyInfoWindow') propertyInfoWindow?: MapInfoWindow;
  @ViewChild('nearbyPlaceInfoWindow') nearbyPlaceInfoWindow?: MapInfoWindow;

  nearbyDistanceKm = 10;
  expandedNearbyCategoryId: string | null = null;
  expandedNearbyCategoryIds: string[] = [];
  showAllNearbyCategories = true;
  selectedNearbyCategoryIds: string[] = [];
  isLoadingNearbyPlaces = false;
  nearbyPlacesError = '';
  nearbyPlacesByCategory: Record<string, NearbyPlace[]> = {};
  selectedNearbyPlace: { category: NearbyPlaceCategory; place: NearbyPlace } | null = null;
  readonly nearbyDistanceMinKm = 0.5;
  readonly nearbyDistanceMaxKm = 10;
  readonly nearbyPlaceCategories: NearbyPlaceCategory[] = [
    { id: 'schools', title: 'Escolas', markerLabel: 'E', googleTypes: ['school', 'university'], googlePrimaryTypes: ['school', 'university'] },
    { id: 'hospitals', title: 'Hospitais', markerLabel: 'H', googleTypes: ['hospital', 'doctor'], googlePrimaryTypes: ['hospital', 'doctor'] },
    { id: 'markets', title: 'Mercados', markerLabel: 'M', googleTypes: ['supermarket', 'grocery_or_supermarket'], googlePrimaryTypes: ['supermarket', 'grocery_store'] },
    { id: 'tourism', title: 'Pontos turísticos', markerLabel: 'T', googleTypes: ['tourist_attraction'], googlePrimaryTypes: ['tourist_attraction'] },
    { id: 'malls', title: 'Shoppings', markerLabel: 'S', googleTypes: ['shopping_mall'], googlePrimaryTypes: ['shopping_mall'] },
    { id: 'trains', title: 'Trens', markerLabel: 'T', googleTypes: ['train_station', 'subway_station'], googlePrimaryTypes: ['train_station', 'subway_station'] },
    { id: 'buses', title: 'Ônibus', markerLabel: 'O', googleTypes: ['bus_station', 'transit_station'], googlePrimaryTypes: ['bus_station'] },
    { id: 'pharmacies', title: 'Farmácias', markerLabel: 'F', googleTypes: ['pharmacy'], googlePrimaryTypes: ['pharmacy'] },
    { id: 'restaurants', title: 'Restaurantes', markerLabel: 'R', googleTypes: ['restaurant'], googlePrimaryTypes: ['restaurant'] },
    { id: 'parks', title: 'Parques', markerLabel: 'P', googleTypes: ['park'], googlePrimaryTypes: ['park'] }
  ];
  ngAfterViewInit(): void {
    this.loadNearbyPlaces();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['realEstate'] && !changes['realEstate'].firstChange) {
      this.loadNearbyPlaces();
    }
  }

  centerMap(): void {
    if (!this.realEstate?.lat || !this.realEstate?.lng || !this.nearbyPlacesMap?.googleMap) {
      return;
    }

    this.nearbyPlacesMap.googleMap.setCenter({ lat: this.realEstate.lat, lng: this.realEstate.lng });
    this.nearbyPlacesMap.googleMap.setZoom(this.getNearbyMapZoom());
  }

  getFilteredNearbyPlaces(category: NearbyPlaceCategory): NearbyPlace[] {
    return (this.nearbyPlacesByCategory[category.id] || []).filter((place) => place.distanceKm <= this.nearbyDistanceKm);
  }

  getNearbyCategoryCount(category: NearbyPlaceCategory): number {
    return this.getFilteredNearbyPlaces(category).length;
  }

  getAllFilteredNearbyPlaces(): Array<{ category: NearbyPlaceCategory; place: NearbyPlace }> {
    return this.getVisibleNearbyCategories().flatMap((category) =>
      this.getFilteredNearbyPlaces(category).map((place) => ({ category, place }))
    );
  }

  getVisibleNearbyCategories(): NearbyPlaceCategory[] {
    if (this.showAllNearbyCategories) {
      return this.nearbyPlaceCategories;
    }

    return this.nearbyPlaceCategories.filter((category) => this.isNearbyCategorySelected(category.id));
  }

  getNearbyPlacePosition(place: NearbyPlace): { lat: number; lng: number } {
    return place.coordinates;
  }

  async loadNearbyPlaces(): Promise<void> {
    if (!this.realEstate?.lat || !this.realEstate?.lng || !this.nearbyPlacesMap?.googleMap) {
      return;
    }

    this.isLoadingNearbyPlaces = true;
    this.nearbyPlacesError = '';
    this.nearbyPlacesByCategory = {};
    const origin = { lat: this.realEstate.lat, lng: this.realEstate.lng };

    try {
      const categoryEntries = await Promise.all(
        this.nearbyPlaceCategories.map(async (category) => [category.id, await this.searchCategoryPlaces(origin, category)] as const)
      );

      this.nearbyPlacesByCategory = Object.fromEntries(categoryEntries);
    } catch (error) {
      console.error('Error loading nearby places from Google Places API:', error);
      this.nearbyPlacesError = 'Não foi possível carregar os locais próximos do Google Maps. Verifique se a Places API está habilitada para a chave configurada.';
    } finally {
      this.isLoadingNearbyPlaces = false;
    }
  }

  private async searchCategoryPlaces(
    origin: google.maps.LatLngLiteral,
    category: NearbyPlaceCategory
  ): Promise<NearbyPlace[]> {
    const placeGroups = await Promise.all(category.googlePrimaryTypes.map((type) => this.searchPlacesByPrimaryType(origin, type)));
    const placesById = new Map<string, NearbyPlace>();

    placeGroups.flat().forEach((place) => {
      const current = placesById.get(place.name);
      if (!current || place.distanceKm < current.distanceKm) {
        placesById.set(place.name, place);
      }
    });

    return [...placesById.values()]
      .filter((place) => place.distanceKm <= this.nearbyDistanceMaxKm)
      .sort((a, b) => a.distanceKm - b.distanceKm);
  }

  private async searchPlacesByPrimaryType(origin: google.maps.LatLngLiteral, type: string): Promise<NearbyPlace[]> {
    try {
      const placesLibrary = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;
      const { places } = await placesLibrary.Place.searchNearby({
        fields: ['displayName', 'formattedAddress', 'location'],
        includedPrimaryTypes: [type],
        locationRestriction: {
          center: origin,
          radius: this.nearbyDistanceMaxKm * 1000
        },
        maxResultCount: 20,
        rankPreference: placesLibrary.SearchNearbyRankPreference.DISTANCE
      });

      return places
        .filter((place) => place.displayName && place.location)
        .map((place) => {
          const coordinates = place.location!.toJSON();
          return {
            name: place.displayName!,
            distanceKm: this.calculateDistanceKm(origin, coordinates),
            neighborhood: place.formattedAddress || undefined,
            coordinates
          };
        });

    } catch (error) {
      console.warn(`Nearby search failed for Google Places primary type "${type}".`, error);
      return [];
    }
  }

  private searchPlacesByType(
    service: google.maps.places.PlacesService,
    origin: google.maps.LatLngLiteral,
    type: string
  ): Promise<NearbyPlace[]> {
    return new Promise((resolve, reject) => {
      service.nearbySearch(
        {
          location: origin,
          radius: this.nearbyDistanceMaxKm * 1000,
          type
        },
        (results, status) => {
          if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            resolve([]);
            return;
          }

          if (status !== google.maps.places.PlacesServiceStatus.OK || !results) {
            reject(status);
            return;
          }

          resolve(results
            .filter((result) => result.name && result.geometry?.location)
            .map((result) => {
              const coordinates = result.geometry!.location!.toJSON();
              return {
                name: result.name!,
                distanceKm: this.calculateDistanceKm(origin, coordinates),
                neighborhood: result.vicinity,
                coordinates
              };
            }));
        }
      );
    });
  }

  private calculateDistanceKm(origin: google.maps.LatLngLiteral, destination: google.maps.LatLngLiteral): number {
    const earthRadiusKm = 6371;
    const latDistance = this.toRadians(destination.lat - origin.lat);
    const lngDistance = this.toRadians(destination.lng - origin.lng);
    const originLat = this.toRadians(origin.lat);
    const destinationLat = this.toRadians(destination.lat);
    const haversine = Math.sin(latDistance / 2) ** 2 + Math.cos(originLat) * Math.cos(destinationLat) * Math.sin(lngDistance / 2) ** 2;

    return earthRadiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  private toRadians(value: number): number {
    return value * Math.PI / 180;
  }

  getNearbyMarkerLabel(category: NearbyPlaceCategory): google.maps.MarkerLabel {
    return { text: category.markerLabel, color: '#ffffff', fontSize: '12px', fontWeight: '600' };
  }

  getPropertyMarkerOptions(): google.maps.MarkerOptions {
    return {
      title: this.realEstate.address,
      label: { text: 'I', color: '#ffffff', fontSize: '12px', fontWeight: '800' },
      zIndex: 1000,
      icon: {
        url: this.getPinMarkerSvgUrl('#dc2626')
      }
    };
  }

  getNearbyMarkerOptions(category: NearbyPlaceCategory, place: NearbyPlace): google.maps.MarkerOptions {
    return {
      title: place.name,
      label: { text: category.markerLabel, color: '#ffffff', fontSize: '11px', fontWeight: '700' },
      zIndex: 100,
      icon: {
        url: this.getPinMarkerSvgUrl('#6b7280')
      }
    };
  }

  private getPinMarkerSvgUrl(fillColor: string): string {
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
        <path d="M14 39C14 39 27 24.5 27 14C27 6.8 21.2 1 14 1C6.8 1 1 6.8 1 14C1 24.5 14 39 14 39Z" fill="${fillColor}" stroke="#ffffff" stroke-width="2"/>
        <circle cx="14" cy="14" r="7.5" fill="${fillColor}"/>
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  toggleNearbyCategory(categoryId: string): void {
    if (this.isMobileNearbyView()) {
      this.expandedNearbyCategoryId = this.expandedNearbyCategoryId === categoryId ? null : categoryId;
      this.expandedNearbyCategoryIds = this.expandedNearbyCategoryId ? [this.expandedNearbyCategoryId] : [];
      return;
    }

    this.expandedNearbyCategoryId = null;
    this.expandedNearbyCategoryIds = this.expandedNearbyCategoryIds.includes(categoryId)
      ? this.expandedNearbyCategoryIds.filter((id) => id !== categoryId)
      : [...this.expandedNearbyCategoryIds, categoryId];
  }

  isNearbyCategoryExpanded(categoryId: string): boolean {
    if (this.isMobileNearbyView()) {
      return this.expandedNearbyCategoryId === categoryId;
    }

    return this.expandedNearbyCategoryIds.includes(categoryId);
  }

  private isMobileNearbyView(): boolean {
    return window.matchMedia('(max-width: 767.98px)').matches;
  }

  isNearbyCategorySelected(categoryId: string): boolean {
    return !this.showAllNearbyCategories && this.selectedNearbyCategoryIds.includes(categoryId);
  }

  areAllNearbyCategoriesSelected(): boolean {
    return this.showAllNearbyCategories;
  }

  selectAllNearbyCategories(): void {
    this.showAllNearbyCategories = true;
    this.selectedNearbyCategoryIds = [];
  }

  toggleNearbyCategoryFilter(categoryId: string): void {
    this.showAllNearbyCategories = false;
    if (this.selectedNearbyCategoryIds.includes(categoryId)) {
      this.selectedNearbyCategoryIds = this.selectedNearbyCategoryIds.filter((id) => id !== categoryId);
    } else {
      this.selectedNearbyCategoryIds = [...this.selectedNearbyCategoryIds, categoryId];
    }
  }

  focusNearbyPlace(place: NearbyPlace): void {
    if (!this.nearbyPlacesMap?.googleMap) {
      return;
    }

    this.nearbyPlacesMap.googleMap.panTo(this.getNearbyPlacePosition(place));
    this.nearbyPlacesMap.googleMap.setZoom(16);
  }

  openPropertyCard(marker: MapMarker): void {
    this.selectedNearbyPlace = null;
    this.nearbyPlaceInfoWindow?.close();
    this.propertyInfoWindow?.open(marker);
  }

  openNearbyPlaceCard(category: NearbyPlaceCategory, place: NearbyPlace, marker: MapMarker): void {
    this.selectedNearbyPlace = { category, place };
    this.propertyInfoWindow?.close();
    this.nearbyPlaceInfoWindow?.open(marker);
  }

  getNearbyMapZoom(): number {
    if (this.nearbyDistanceKm <= 2) {
      return 15;
    }

    if (this.nearbyDistanceKm <= 8) {
      return 13;
    }

    return 11;
  }

  getPropertyImage(): string | null {
    if (Array.isArray(this.realEstate?.images) && this.realEstate.images.length > 0) {
      return this.realEstate.images[0];
    }

    return null;
  }

  getPropertyPrice(): number | undefined {
    return this.realEstate?.auction_2_price || this.realEstate?.auction_1_price || this.realEstate?.minimum_selling_price || this.realEstate?.assessment_price;
  }
}
